import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './MyNotes.css';
import Note from './Note';
import { getAllPosts, getSavedPosts, getMyYearPlans } from '../../actions/postAction';
import Loader from '../../layout/Loder';
import TokenPopup from './TokenPopup';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import PlanCard from './PlanCard';
import GettingStarted, { STEPS_IDS } from './GettingStarted';
import { updateChecklist } from '../../actions/userAction';
import FirstVisionWelcome from './FirstVisionWelcome';

const MyNotes = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { loading, posts, totalPages, totalPosts } =
    useSelector((state) => state.posts);

  const { plans, loading: plansLoading } =
    useSelector((state) => state.myYearlyPlan || {});

  const { user } = useSelector((state) => state.user);

  const [checklistCompleted, setChecklistCompleted] = useState(
    () => new Set(user?.checklistCompleted || [])
  );


  const handleToggle = (id) => {
    const nowDone = !checklistCompleted.has(id);
    const next = new Set(checklistCompleted);
    if (nowDone) next.add(id); else next.delete(id);
    setChecklistCompleted(next);
    dispatch(updateChecklist(id, nowDone));
    if (STEPS_IDS.every(sid => next.has(sid))) {
      setActiveView("plans");
    }
  };

  const [activeView, setActiveView] = useState(
    location.state?.activeView || (user?.checklistCompleted?.length === STEPS_IDS.length ? "plans" : "checklist")
  );
  const didAutoSwitchToPlans = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const hasFetchedNodes = useRef(false);

  const getPosts = async (page = 1) => {
    const res = await dispatch(getAllPosts(page, "side-quest"));
    if (res?.status === 401) {
      toast.error('Log in to access this event');
      window.open('/', '_self');
    }
  };

  // Fetch only once per mount cycle
  useEffect(() => {
    if (activeView === "nodes" && !hasFetchedNodes.current) {
      hasFetchedNodes.current = true;
      setCurrentPage(1);
      getPosts(1);
    }
  }, [activeView]);

  // Fetch additional pages when currentPage increments
  useEffect(() => {
    if (activeView === "nodes" && currentPage > 1) {
      const fetchMore = async () => {
        setLoadingMore(true);
        await getPosts(currentPage);
        setLoadingMore(false);
      };
      fetchMore();
    }
  }, [currentPage]);

  useEffect(() => {
    dispatch(getSavedPosts());
    dispatch(getMyYearPlans());
  }, [dispatch]);

  // If plans load and user is stuck on checklist view (no explicit preference), switch to plans
  useEffect(() => {
    if (!plansLoading && plans?.length > 0 && activeView === "checklist" && !location.state?.activeView && !didAutoSwitchToPlans.current) {
      didAutoSwitchToPlans.current = true;
      setActiveView("plans");
    }
  }, [plansLoading, plans]);

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const hasMore = currentPage < totalPages;


  return (
    <div>
      <Helmet>
        <title>My Workspace</title>
      </Helmet>

      {/* ================= TOP BAR ================= */}
      <div className="dashboard-top-row">
        <div className="top-left">
          {!STEPS_IDS.every(id => checklistCompleted.has(id)) && (
            <div className="view-toggle">
              <button
                className={activeView === "checklist" ? "active" : ""}
                onClick={() => setActiveView("checklist")}
              >
                Getting started
              </button>
            </div>
          )}
          <div className="view-toggle">
            <button
              className={activeView === "plans" ? "active" : ""}
              onClick={() => setActiveView("plans")}
            >
              My Visions
            </button>
            <button
              className={activeView === "nodes" ? "active" : ""}
              onClick={() => setActiveView("nodes")}
            >
              Side quests
            </button>
          </div>
        </div>

        {/* <div className="top-right">
          <div className="d-credit-wrapper">
            <div className="d-credit-box">
              <div className="d-left">
                <span className="d-circle">D</span>
                <span className="d-value">{user?.tokens ?? 0}</span>
              </div>
              <div
                className="d-action"
                onClick={() => setShowPopup(true)}
                role="button"
                aria-label="Add credits"
              >
                +
              </div>
            </div>
          </div>

          <Link
            to={activeView === "nodes" ? "/dashboard/side-quest/new" : "/dashboard/year-plans"}
            className="btn btn-orange"
            onClick={() => {
              // Reset cache so new post appears on next visit
              if (activeView === "nodes") hasFetchedNodes.current = false;
            }}
          >
            <span className="new-plus">+</span> New
          </Link>
        </div> */}
      </div>

      {/* ================= CHECKLIST ================= */}
      {activeView === "checklist" && (
        <GettingStarted completed={checklistCompleted} onToggle={handleToggle} />
      )}

      {/* ================= PLANS ================= */}
      {activeView === "plans" && (
        <div className="my-plans-section">
          {plansLoading ? (
            <Loader />
          ) : plans.length === 0 ? (
            <FirstVisionWelcome userName={user?.name} />
          ) : (
            <div className="plans-grid">
              {plans.map((plan) => (
                <PlanCard key={plan._id} plan={plan} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= NODES ================= */}
      {activeView === "nodes" && (
        loading && currentPage === 1 ? <Loader /> : (
          <div className="mynodes">
            <div className="notes-list">
              {posts.length === 0 && !loading ? (
  <div className="sq-empty-state">
    <Link to="/dashboard/side-quest/new" className="sq-empty-box">
      <div className="sq-empty-box-top">
        <span className="sq-empty-box-label">side quest</span>
      </div>
      <div className="sq-empty-box-body">
        <span className="sq-empty-box-cursor" />
        <span className="sq-empty-box-placeholder">a random trip, learn cooking, anything just for fun…</span>
      </div>
      <div className="sq-empty-box-foot">
        <span className="sq-empty-box-def">side quests are everything outside your main visions. Free, low-stakes, just yours.</span>
        <span className="sq-empty-box-cta">Begin →</span>
      </div>
    </Link>
  </div>
) : (
  posts.map((post) => (
    <Note key={post._id} post={post} />
  ))
)}

              {/* LOAD MORE */}
              {hasMore && (
                <div className="load-more-row">
                  {loadingMore ? (
                    <span className="load-more-spinner">Loading...</span>
                  ) : (
                    <button className="load-more-btn" onClick={handleLoadMore}>
                      Load more
                    </button>
                  )}
                </div>
              )}

              {/* END OF LIST */}
              {!hasMore && posts.length > 0 && (
                <div className="list-end">
                  <span>You've seen all your SQs</span>
                </div>
              )}

            </div>
          </div>
        )
      )}

      {/* ================= MOBILE FAB ================= */}
      <div className="mobile-fab-container">
        <Link
          to={activeView === "nodes" ? "/dashboard/side-quest/new" : "/dashboard/year-plans"}
          className="mobile-fab primary"
          onClick={() => {
            if (activeView === "nodes") hasFetchedNodes.current = false;
          }}
        >
          +
        </Link>
      </div>

      {showPopup && (
        <TokenPopup
          tokenCount={user?.tokens ?? 0}
          updateTokenCount={() => {}}
          closePopup={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default MyNotes;
